import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import { DefaultScores } from '../../constants';
import type { PsychologicalResult } from '../../model';
import formatScoreOutOf from '../formatters';
import { growthMindsetHeight } from '.';
import type { GrowthMindsetAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/GrowthMindset/GrowthMindset.api';

const parseGrowthMindset = (t: TFunction, items: AssessmentResultItemDataFragment[]): PsychologicalResult | null => {
    const item = items.find(i => i.result.__typename === 'GrowthMindsetResult');
    if (!item) {
        return null;
    }

    const { moduleInstance } = item;
    const growthMindsetResult = item.result as GrowthMindsetAssessmentResultDataFragment;

    if (!moduleInstance?.moduleDefinitionIdentifier) {
        return null;
    }

    const categories = growthMindsetResult?.categoryAverages || [];
    if (categories.length === 0) {
        return null;
    }

    const radarData = categories.map(c => ({
        axis: c.category,
        individual: Number(c.averageScore.toFixed(1)),
    }));
    const hasValidMax = categories.some(c => (c.maximumPossibleScore ?? 0) > 0);
    const maxDomain = hasValidMax
        ? Math.max(1, ...categories.map(c => c.maximumPossibleScore ?? 0))
        : DefaultScores.GrowthMindsetMaxScore;

    return {
        moduleDefinitionIdentifier: moduleInstance.moduleDefinitionIdentifier,
        name: moduleInstance.name ?? '',
        description: moduleInstance.description ?? '',
        radarGroups: [
            {
                key: 'growth-mindset',
                scoreCards: categories.map(c => ({
                    label: c.category,
                    value: formatScoreOutOf(t, c.averageScore, c.maximumPossibleScore, 1),
                    badge: c.label,
                })),
                charts: [
                    {
                        data: radarData,
                        maxDomain,
                        height: growthMindsetHeight,
                        variant: 'bare',
                    },
                ],
            },
        ],
    };
};

export default parseGrowthMindset;
